"use client";

import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useRef,
} from "react";
import { BASE_URL } from "../config/apiconfig";
import { useAuth } from "./AuthContext";

const SizesContext = createContext();
export const useSizes = () => useContext(SizesContext);

export const SizesProvider = ({ children }) => {
  const { token, adminId, isReady, logout } = useAuth();

  const [sizes, setSizes] = useState([]);
  const [sizesLoading, setSizesLoading] = useState(false);

  const mountedRef = useRef(true);

  const SIZES_ENDPOINT = "/sizes";
  const baseURL = BASE_URL.replace(/\/$/, "");

  /* ---------------- Mount Safety ---------------- */
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const safeSetsizes = (value) => {
    if (mountedRef.current) setSizes(value);
  };

  /* ---------------- FETCH ---------------- */
  const fetchSizes = useCallback(async () => {
    if (!token) return;
    setSizesLoading(true);
    try {
      const res = await fetch(`${baseURL}${SIZES_ENDPOINT}`, {
        method: "GET",
        headers: { Authorization: `Bearer ${token}`, Accept: "application/json" },
      });
      const payload = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(payload?.message || "Fetch failed");
      safeSetsizes(payload?.data || []);
      return payload;
    } catch (err) {
      console.error("[Sizes] fetch error:", err);
      if (err.message?.toLowerCase().includes("token")) logout();
      safeSetsizes([]);
    } finally {
      if (mountedRef.current) setSizesLoading(false);
    }
  }, [token, baseURL, logout]);

  /* ---------------- CREATE ---------------- */
  const addSize = useCallback(
    async (data) => {
      try {
        const res = await fetch(`${baseURL}${SIZES_ENDPOINT}`, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            ...data,
            create_audit_id: adminId,
          }),
        });

        const payload = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(payload?.message || "Create failed");

        await fetchSizes();
        return payload;
      } catch (err) {
        console.error("[Sizes] add error:", err);
        throw err;
      }
    },
    [token, adminId, fetchSizes]
  );

  /* ---------------- UPDATE ---------------- */
  const updateSize = useCallback(
    async (id, data) => {
      try {
        const res = await fetch(`${baseURL}${SIZES_ENDPOINT}/${id}`, {
          method: "PUT",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            ...data,
            update_audit_id: adminId,
          }),
        });

        const payload = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(payload?.message || "Update failed");

        await fetchSizes();
        return payload;
      } catch (err) {
        console.error("[Sizes] update error:", err);
        throw err;
      }
    },
    [token, adminId, fetchSizes]
  );

  /* ---------------- DELETE ---------------- */
  const deleteSize = useCallback(
    async (id) => {
      try {
        const res = await fetch(`${baseURL}${SIZES_ENDPOINT}/${id}`, {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            delete_audit_id: adminId,
          }),
        });

        const payload = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(payload?.message || "Delete failed");

        safeSetsizes((prev) =>
          prev.filter((s) => String(s.id) !== String(id))
        );

        return payload;
      } catch (err) {
        console.error("[Sizes] delete error:", err);
        throw err;
      }
    },
    [token, adminId]
  );

  /* ---------------- STATUS ---------------- */
  const updateSizeStatus = useCallback(
    async (id, status) => {
      try {
        const res = await fetch(`${baseURL}${SIZES_ENDPOINT}/${id}/status`, {
          method: "PATCH",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            status,
            update_audit_id: adminId,
          }),
        });

        const payload = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(payload?.message || "Status update failed");

        safeSetsizes((prev) =>
          prev.map((s) =>
            String(s.id) === String(id) ? { ...s, status } : s
          )
        );

        return payload;
      } catch (err) {
        console.error("[Sizes] status error:", err);
        throw err;
      }
    },
    [token, adminId]
  );

  /* ---------------- INIT LOAD ---------------- */
  useEffect(() => {
    if (!isReady || !token) return;
    fetchSizes().catch(() => {});
  }, [isReady, token, fetchSizes]);

  return (
    <SizesContext.Provider
      value={{
        sizes,
        sizesLoading,
        fetchSizes,
        addSize,
        updateSize,
        deleteSize,
        updateSizeStatus,
      }}
    >
      {children}
    </SizesContext.Provider>
  );
};